<template>
  <div class="estimates-page" :class="{ 'mode-ledger': mode === 'ledger', 'mode-create': mode === 'create' }">
    <div class="aurora" aria-hidden="true">
      <span class="blob b1" />
      <span class="blob b2" />
      <span class="blob b3" />
      <span class="grid-lines" />
    </div>

    <header class="hero">
      <div class="hero-copy">
        <p class="eyebrow">Chibitronics</p>
        <h1>Conduit &amp; Estimates</h1>
        <p class="lede">Track potential sales from first conversation through purchase order — and craft quotes along the way.</p>
      </div>
      <div class="mode-tabs" role="tablist" aria-label="Conduit & Estimates mode">
        <button
          type="button"
          role="tab"
          class="mode-tab"
          :class="{ active: mode === 'ledger' }"
          :aria-selected="mode === 'ledger'"
          @click="setMode('ledger')"
        >
          Ledger
        </button>
        <button
          type="button"
          role="tab"
          class="mode-tab"
          :class="{ active: mode === 'create' }"
          :aria-selected="mode === 'create'"
          @click="setMode('create')"
        >
          Create
        </button>
      </div>
    </header>

    <!-- ═══════════════ LEDGER ═══════════════ -->
    <section v-if="mode === 'ledger'" class="ledger" aria-label="All estimates and orders">
      <div class="conduit-pipeline" aria-label="Conduit pipeline summary">
        <div class="pipeline-head">
          <h2>Conduit pipeline</h2>
          <p class="pipeline-sub">
            {{ pipelineOpenCount }} open opportunit{{ pipelineOpenCount === 1 ? 'y' : 'ies' }}
            · ${{ money(pipelineOpenTotal) }} potential
          </p>
        </div>
        <div class="pipeline-stages">
          <button
            v-for="stage in conduitSummary"
            :key="stage.level"
            type="button"
            class="pipeline-stage"
            :class="{ active: conduitFilter === stage.level }"
            @click="toggleConduitFilter(stage.level)"
          >
            <span class="stage-level">{{ stage.level }}</span>
            <span class="stage-label">{{ stage.label }}</span>
            <span class="stage-count">{{ stage.count }}</span>
            <span class="stage-total">${{ money(stage.total) }}</span>
          </button>
        </div>
      </div>

      <div class="ledger-toolbar">
        <div class="type-filter" role="group" aria-label="Document type">
          <button
            type="button"
            class="type-chip"
            :class="{ active: typeFilter === 'all' }"
            @click="typeFilter = 'all'"
          >All</button>
          <button
            type="button"
            class="type-chip estimate"
            :class="{ active: typeFilter === 'estimate' }"
            @click="typeFilter = 'estimate'"
          >Estimates</button>
          <button
            type="button"
            class="type-chip order"
            :class="{ active: typeFilter === 'order' }"
            @click="typeFilter = 'order'"
          >Orders</button>
        </div>
        <label class="search-wrap">
          <span class="sr-only">Search ledger</span>
          <input
            v-model.trim="ledgerQuery"
            type="search"
            placeholder="Search name, company, #, reference…"
            @input="onLedgerSearch"
          />
        </label>
      </div>

      <p v-if="ledgerLoading" class="status-line">Gathering the ledger…</p>
      <p v-else-if="ledgerError" class="status-line error">{{ ledgerError }}</p>
      <p v-else class="status-line">
        <span class="pulse-dot" aria-hidden="true" />
        {{ filteredLedger.length }}
        {{ typeFilter === 'order' ? 'order' : typeFilter === 'estimate' ? 'estimate' : 'document' }}{{ filteredLedger.length === 1 ? '' : 's' }}
        <template v-if="conduitFilter"> at level {{ conduitFilter }}</template>
        <template v-if="ledgerQuery"> matching “{{ ledgerQuery }}”</template>
      </p>

      <div v-if="!ledgerLoading && !ledgerError" class="ledger-stream">
        <article
          v-for="(e, idx) in filteredLedger"
          :key="e.id"
          class="doc-row"
          :style="{ '--i': idx }"
        >
          <div class="doc-rail" aria-hidden="true">
            <span class="doc-node" :class="e.document_type" />
          </div>
          <div class="doc-body">
            <div class="doc-top">
              <div class="doc-id">
                <span class="doc-type" :class="e.document_type">{{ e.document_type }}</span>
                <span class="doc-num">#{{ e.estimate_number }}</span>
                <span class="conduit-badge" :data-level="e.conduit_level || 1">
                  L{{ e.conduit_level || 1 }}
                </span>
              </div>
              <div class="doc-total">${{ money(e.total) }}</div>
            </div>
            <div class="doc-meta">
              <router-link
                v-if="e.customer"
                class="contact-link"
                :to="`/customers/${e.customer.id}`"
              >
                {{ customerName(e.customer) }}
              </router-link>
              <span v-else class="muted">Unknown contact</span>
              <span v-if="e.customer?.company" class="sep">·</span>
              <span v-if="e.customer?.company" class="company">{{ e.customer.company }}</span>
            </div>
            <div class="doc-sub">
              <time :datetime="e.created_at">{{ formatWhen(e.created_at) }}</time>
              <span v-if="e.reference" class="ref">{{ e.reference }}</span>
              <span v-if="e.chibi_contact_name" class="by">Chibi: {{ e.chibi_contact_name }}</span>
              <span v-else-if="e.created_by_email" class="by">by {{ shortEmail(e.created_by_email) }}</span>
            </div>
            <div class="conduit-inline">
              <label class="sr-only" :for="`conduit-${e.id}`">Conduit level</label>
              <select
                :id="`conduit-${e.id}`"
                class="conduit-select"
                :value="e.conduit_level || 1"
                :disabled="updatingId === e.id"
                @change="updateConduitLevel(e, $event.target.value)"
              >
                <option v-for="lvl in conduitLevels" :key="lvl.level" :value="lvl.level">
                  {{ lvl.level }}. {{ lvl.label }}
                </option>
              </select>
            </div>
            <ul v-if="(e.line_items || []).length" class="doc-lines">
              <li v-for="(row, ri) in previewLines(e)" :key="`${e.id}-l-${ri}`">
                {{ row.quantity }}× {{ row.sku || row.title }}
              </li>
              <li v-if="(e.line_items || []).length > 3" class="more">
                +{{ e.line_items.length - 3 }} more
              </li>
            </ul>
          </div>
          <div class="doc-actions">
            <a class="action pdf" :href="e.pdf_url" target="_blank" rel="noopener">PDF</a>
            <button
              type="button"
              class="action danger"
              :disabled="deletingId === e.id"
              @click="deleteFromLedger(e)"
            >
              {{ deletingId === e.id ? '…' : 'Delete' }}
            </button>
          </div>
        </article>

        <p v-if="!filteredLedger.length" class="empty-ledger">
          No documents here yet. Switch to <button type="button" class="inline-link" @click="setMode('create')">Create</button> to make the first one.
        </p>
      </div>
    </section>

    <!-- ═══════════════ CREATE ═══════════════ -->
    <section v-else class="create" aria-label="Create estimate or order">
      <div class="panel conduit-panel">
        <h2 class="panel-title">Conduit status</h2>
        <p class="panel-hint">Where is this opportunity in the sales path?</p>
        <div class="conduit-level-picker" role="group" aria-label="Conduit level">
          <button
            v-for="lvl in conduitLevels"
            :key="lvl.level"
            type="button"
            class="level-chip"
            :class="{ active: Number(form.conduit_level) === lvl.level }"
            @click="form.conduit_level = lvl.level"
          >
            <span class="level-num">{{ lvl.level }}</span>
            <span class="level-text">{{ lvl.label }}</span>
          </button>
        </div>
        <div class="field chibi-contact-field">
          <label>Chibitronics Contact</label>
          <select v-model="form.chibi_contact_name" @change="onChibiContactChange">
            <option value="">— choose —</option>
            <option v-for="m in chibiTeam" :key="m.name" :value="m.name">
              {{ m.name }} · {{ m.email }}
            </option>
          </select>
          <p v-if="form.chibi_contact_name" class="chibi-preview">
            {{ form.chibi_contact_name }}
            <span v-if="form.chibi_contact_email"> · {{ form.chibi_contact_email }}</span>
          </p>
        </div>
      </div>

      <div class="panel">
        <div class="row">
          <div class="field grow">
            <label>Contact search</label>
            <input v-model.trim="customerQuery" placeholder="Search by name or email" @input="searchCustomers" />
            <div v-if="customerOptions.length" class="suggestions">
              <button
                v-for="c in customerOptions"
                :key="c.id"
                type="button"
                class="suggestion"
                @click="selectCustomer(c)"
              >
                {{ customerLabel(c) }}
              </button>
            </div>
          </div>
          <div class="field">
            <label>Type</label>
            <select v-model="form.document_type">
              <option value="estimate">Estimate</option>
              <option value="order">Order</option>
            </select>
          </div>
        </div>

        <div v-if="selectedCustomer" class="selected-contact">
          For:
          <router-link :to="`/customers/${selectedCustomer.id}`">{{ customerLabel(selectedCustomer) }}</router-link>
        </div>

        <div class="row">
          <div class="field grow">
            <label>Billing address</label>
            <textarea v-model="form.billing_address" rows="4"></textarea>
          </div>
          <div class="field grow">
            <label>Shipping address</label>
            <textarea v-model="form.shipping_address" rows="4"></textarea>
          </div>
        </div>

        <div class="row">
          <div class="field grow">
            <label>Reference</label>
            <input v-model.trim="form.reference" placeholder="PO# or custom reference" />
          </div>
          <div class="field">
            <label>Shipping</label>
            <input v-model.number="form.shipping_amount" type="number" step="0.01" min="0" />
          </div>
          <div class="field discount-field">
            <label>Discount</label>
            <div class="discount-mode" role="group" aria-label="Discount type">
              <button
                type="button"
                class="discount-mode-btn"
                :class="{ active: discountMode === 'percent' }"
                @click="discountMode = 'percent'"
              >%</button>
              <button
                type="button"
                class="discount-mode-btn"
                :class="{ active: discountMode === 'amount' }"
                @click="discountMode = 'amount'"
              >$</button>
            </div>
            <input
              v-if="discountMode === 'percent'"
              v-model.number="discountPercent"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder="0"
            />
            <input
              v-else
              v-model.number="form.discount_amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
            />
            <p v-if="discountMode === 'percent' && effectiveDiscountAmount > 0" class="discount-equiv">
              = ${{ money(effectiveDiscountAmount) }} off subtotal
            </p>
          </div>
          <div class="field">
            <label>Discount label</label>
            <input v-model.trim="form.discount_label" placeholder="e.g. Educator discount" />
          </div>
        </div>
      </div>

      <div class="panel">
        <div class="row">
          <h2>Items</h2>
          <div class="field grow">
            <label>Find Shopify item (code or name)</label>
            <input v-model.trim="variantQuery" @input="searchVariants" placeholder="Type SKU or title…" />
            <div v-if="variantOptions.length" class="suggestions">
              <button
                v-for="v in variantOptions"
                :key="v.id"
                type="button"
                class="suggestion"
                @click="addVariant(v)"
              >
                {{ v.sku || '—' }} · {{ v.title }} · ${{ money(v.unit_price) }}
              </button>
            </div>
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in form.line_items" :key="`row-${idx}`">
              <td><input v-model.trim="row.sku" /></td>
              <td><input v-model.trim="row.title" /></td>
              <td><input v-model.number="row.quantity" type="number" min="1" step="1" /></td>
              <td><input v-model.number="row.unit_price" type="number" min="0" step="0.01" /></td>
              <td>${{ money(lineAmount(row)) }}</td>
              <td><button type="button" class="danger-btn" @click="removeLine(idx)">✕</button></td>
            </tr>
          </tbody>
        </table>
        <button type="button" class="ghost" @click="addEmptyLine">+ Add line</button>

        <div class="totals">
          <div>Subtotal: <strong>${{ money(subtotal) }}</strong></div>
          <div>Shipping: <strong>${{ money(form.shipping_amount || 0) }}</strong></div>
          <div v-if="effectiveDiscountAmount > 0">
            Discount<span v-if="discountMode === 'percent' && discountPercent"> ({{ discountPercent }}%)</span>:
            <strong>- ${{ money(effectiveDiscountAmount) }}</strong>
          </div>
          <div class="grand">Total: <strong>${{ money(total) }}</strong></div>
        </div>

        <div class="actions">
          <button type="button" class="primary" :disabled="saving || !selectedCustomer" @click="saveEstimate">
            {{ saving ? 'Creating…' : 'Create & Open PDF' }}
          </button>
        </div>
        <p v-if="errorMsg" class="form-msg error">{{ errorMsg }}</p>
        <p v-if="successMsg" class="form-msg success">{{ successMsg }}</p>
      </div>
    </section>
  </div>
</template>

<script>
import CustomersService from '@/services/CustomersService'
import EstimatesService, { CONDUIT_LEVELS } from '@/services/EstimatesService'
import chibiTeamMixin from '@/mixins/chibiTeamMixin'

export default {
  name: 'EstimatesPage',
  mixins: [chibiTeamMixin],
  data() {
    const fromContact = Boolean(this.$route.query.customer_id)
    return {
      mode: fromContact ? 'create' : 'ledger',
      ledger: [],
      ledgerLoading: false,
      ledgerError: '',
      ledgerQuery: '',
      typeFilter: 'all',
      conduitFilter: null,
      conduitSummary: CONDUIT_LEVELS.map((l) => ({ ...l, count: 0, total: 0 })),
      conduitLevels: CONDUIT_LEVELS,
      searchTimer: null,
      deletingId: null,
      updatingId: null,

      customerQuery: '',
      customerOptions: [],
      selectedCustomer: null,
      variantQuery: '',
      variantOptions: [],
      saving: false,
      errorMsg: '',
      successMsg: '',
      discountMode: 'percent',
      discountPercent: 0,
      form: {
        document_type: 'estimate',
        conduit_level: 1,
        chibi_contact_name: '',
        chibi_contact_email: '',
        billing_address: '',
        shipping_address: '',
        reference: '',
        shipping_amount: 0,
        discount_amount: 0,
        discount_label: '',
        line_items: [{ sku: '', title: '', quantity: 1, unit_price: 0 }]
      }
    }
  },
  computed: {
    filteredLedger() {
      let rows = this.ledger
      if (this.typeFilter !== 'all') {
        rows = rows.filter((e) => e.document_type === this.typeFilter)
      }
      if (this.conduitFilter) {
        rows = rows.filter((e) => Number(e.conduit_level || 1) === Number(this.conduitFilter))
      }
      return rows
    },
    pipelineOpenCount() {
      return this.conduitSummary
        .filter((s) => s.level < 5)
        .reduce((acc, s) => acc + Number(s.count || 0), 0)
    },
    pipelineOpenTotal() {
      return this.conduitSummary
        .filter((s) => s.level < 5)
        .reduce((acc, s) => acc + Number(s.total || 0), 0)
    },
    subtotal() {
      return this.form.line_items.reduce((acc, row) => acc + this.lineAmount(row), 0)
    },
    effectiveDiscountAmount() {
      if (this.discountMode === 'percent') {
        const pct = Math.min(100, Math.max(0, Number(this.discountPercent || 0)))
        return Math.round(this.subtotal * (pct / 100) * 100) / 100
      }
      return Math.max(0, Number(this.form.discount_amount || 0))
    },
    total() {
      return this.subtotal + Number(this.form.shipping_amount || 0) - this.effectiveDiscountAmount
    }
  },
  created() {
    const customerId = this.$route.query.customer_id
    if (customerId) this.loadCustomer(customerId)
    if (this.mode === 'ledger') this.loadLedger()
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    setMode(mode) {
      this.mode = mode
      if (mode === 'ledger' && !this.ledger.length && !this.ledgerLoading) {
        this.loadLedger()
      }
    },
    toggleConduitFilter(level) {
      this.conduitFilter = this.conduitFilter === level ? null : level
    },
    onLedgerSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.loadLedger(), 280)
    },
    async loadLedger() {
      this.ledgerLoading = true
      this.ledgerError = ''
      try {
        const params = {}
        if (this.ledgerQuery) params.q = this.ledgerQuery
        const { data } = await EstimatesService.listAll(params)
        this.ledger = data.estimates || []
        if (Array.isArray(data.conduit_summary) && data.conduit_summary.length) {
          this.conduitSummary = data.conduit_summary
        } else {
          this.conduitSummary = this.buildLocalSummary(this.ledger)
        }
        if (Array.isArray(data.conduit_levels) && data.conduit_levels.length) {
          this.conduitLevels = data.conduit_levels
        }
        if (Array.isArray(data.chibi_team) && data.chibi_team.length) {
          this.chibiTeam = data.chibi_team
        }
      } catch {
        this.ledgerError = 'Could not load estimates.'
        this.ledger = []
      } finally {
        this.ledgerLoading = false
      }
    },
    buildLocalSummary(rows) {
      return CONDUIT_LEVELS.map((lvl) => {
        const matches = (rows || []).filter((e) => Number(e.conduit_level || 1) === lvl.level)
        return {
          ...lvl,
          count: matches.length,
          total: matches.reduce((acc, e) => acc + Number(e.total || 0), 0)
        }
      })
    },
    customerName(c) {
      return [c.first_name, c.last_name].filter(Boolean).join(' ') || '—'
    },
    customerLabel(c) {
      const name = this.customerName(c)
      return `${name} (${c.main_email || 'no email'})`
    },
    previewLines(e) {
      return (e.line_items || []).slice(0, 3)
    },
    shortEmail(email) {
      const s = String(email || '')
      const at = s.indexOf('@')
      return at > 0 ? s.slice(0, at) : s
    },
    formatWhen(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
      } catch {
        return iso
      }
    },
    lineAmount(row) {
      return Number(row.quantity || 0) * Number(row.unit_price || 0)
    },
    money(v) {
      const n = Number(v || 0)
      return Number.isFinite(n) ? n.toFixed(2) : '0.00'
    },
    onChibiContactChange() {
      const match = this.chibiTeam.find((m) => m.name === this.form.chibi_contact_name)
      this.form.chibi_contact_email = match ? match.email : ''
    },
    addEmptyLine() {
      this.form.line_items.push({ sku: '', title: '', quantity: 1, unit_price: 0 })
    },
    removeLine(idx) {
      this.form.line_items.splice(idx, 1)
      if (!this.form.line_items.length) this.addEmptyLine()
    },
    async searchCustomers() {
      const q = this.customerQuery.trim()
      if (q.length < 2) {
        this.customerOptions = []
        return
      }
      try {
        const { data } = await CustomersService.list({ q, page: 1, per_page: 8 })
        this.customerOptions = data.customers || []
      } catch {
        this.customerOptions = []
      }
    },
    async loadCustomer(id) {
      try {
        const { data } = await CustomersService.getById(id)
        this.selectCustomer(data)
      } catch {
        // ignore invalid query param
      }
    },
    selectCustomer(c) {
      this.selectedCustomer = c
      this.customerQuery = this.customerLabel(c)
      this.customerOptions = []
      const baseAddress = [c.company, [c.first_name, c.last_name].filter(Boolean).join(' '), c.billing_address].filter(Boolean).join('\n')
      this.form.billing_address = this.form.billing_address || baseAddress
      this.form.shipping_address = this.form.shipping_address || baseAddress
      if (!this.form.chibi_contact_name && c.chibi_contact) {
        this.form.chibi_contact_name = c.chibi_contact
        this.onChibiContactChange()
      }
    },
    async searchVariants() {
      const q = this.variantQuery.trim()
      if (q.length < 2) {
        this.variantOptions = []
        return
      }
      try {
        const { data } = await EstimatesService.searchVariants(q)
        this.variantOptions = data.variants || []
      } catch {
        this.variantOptions = []
      }
    },
    addVariant(v) {
      this.form.line_items.push({
        sku: v.sku || '',
        title: v.title || '',
        quantity: 1,
        unit_price: Number(v.unit_price || 0)
      })
      this.variantQuery = ''
      this.variantOptions = []
    },
    async updateConduitLevel(e, level) {
      if (!e?.id || !e?.customer_id) return
      const next = Number(level)
      if (!next || next === Number(e.conduit_level)) return
      this.updatingId = e.id
      try {
        const { data } = await EstimatesService.update(e.customer_id, e.id, { conduit_level: next })
        const idx = this.ledger.findIndex((x) => x.id === e.id)
        if (idx >= 0) this.ledger.splice(idx, 1, { ...this.ledger[idx], ...data })
        this.conduitSummary = this.buildLocalSummary(this.ledger)
      } catch {
        alert('Could not update conduit level.')
      } finally {
        this.updatingId = null
      }
    },
    async deleteFromLedger(e) {
      if (!e?.id || !e?.customer_id) return
      const label = `#${e.estimate_number} (${e.document_type})`
      if (!confirm(`Delete estimate ${label}? This cannot be undone.`)) return
      this.deletingId = e.id
      try {
        await EstimatesService.destroy(e.customer_id, e.id)
        this.ledger = this.ledger.filter((x) => x.id !== e.id)
        this.conduitSummary = this.buildLocalSummary(this.ledger)
      } catch {
        alert('Could not delete estimate.')
      } finally {
        this.deletingId = null
      }
    },
    async saveEstimate() {
      this.errorMsg = ''
      this.successMsg = ''
      if (!this.selectedCustomer) {
        this.errorMsg = 'Pick a contact first.'
        return
      }
      if (!this.form.chibi_contact_name) {
        this.errorMsg = 'Choose a Chibitronics Contact.'
        return
      }
      const discountAmount = this.effectiveDiscountAmount
      let discountLabel = String(this.form.discount_label || '').trim()
      if (!discountLabel && this.discountMode === 'percent' && Number(this.discountPercent || 0) > 0) {
        discountLabel = `${Number(this.discountPercent)}% discount`
      }
      const payload = {
        ...this.form,
        discount_amount: discountAmount,
        discount_label: discountLabel,
        conduit_level: Number(this.form.conduit_level || 1),
        line_items: this.form.line_items
          .map(i => ({ sku: String(i.sku || '').trim(), title: String(i.title || '').trim(), quantity: Number(i.quantity || 0), unit_price: Number(i.unit_price || 0) }))
          .filter(i => i.sku || i.title)
      }
      if (!payload.line_items.length) {
        this.errorMsg = 'Add at least one line item.'
        return
      }

      this.saving = true
      try {
        const { data } = await EstimatesService.create(this.selectedCustomer.id, payload)
        this.successMsg = `${data.document_type} #${data.estimate_number} created`
        window.open(data.pdf_url, '_blank', 'noopener')
        this.ledger = []
        this.setMode('ledger')
        await this.loadLedger()
      } catch (err) {
        this.errorMsg = err.response?.data?.errors?.join?.('; ') || 'Could not create estimate.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');

.estimates-page {
  --ink: #e8f4f1;
  --muted: rgba(232, 244, 241, 0.62);
  --sea: #1abc9c;
  --sea-deep: #0e7a66;
  --amber: #e8b86d;
  --ink-blue: #5dade2;
  --danger: #e57373;
  position: relative;
  min-height: calc(100vh - 4rem);
  padding: 0.75rem 0.85rem 2.5rem;
  color: var(--ink);
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  overflow: hidden;
}

.aurora {
  pointer-events: none;
  position: absolute;
  inset: -10% -5% auto -5%;
  height: 420px;
  z-index: 0;
}
.blob {
  position: absolute;
  border-radius: 40% 60% 55% 45%;
  filter: blur(48px);
  opacity: 0.35;
  animation: drift 18s ease-in-out infinite alternate;
}
.b1 {
  width: 340px;
  height: 260px;
  left: 8%;
  top: 20px;
  background: radial-gradient(circle at 30% 40%, rgba(26, 188, 156, 0.55), transparent 70%);
}
.b2 {
  width: 280px;
  height: 220px;
  right: 12%;
  top: 40px;
  background: radial-gradient(circle at 60% 50%, rgba(93, 173, 226, 0.45), transparent 70%);
  animation-delay: -6s;
  animation-duration: 22s;
}
.b3 {
  width: 200px;
  height: 180px;
  left: 42%;
  top: 80px;
  background: radial-gradient(circle at 50% 50%, rgba(232, 184, 109, 0.28), transparent 70%);
  animation-delay: -11s;
  animation-duration: 26s;
}
.grid-lines {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.55), transparent 85%);
  animation: gridPulse 12s ease-in-out infinite;
}

@keyframes drift {
  from { transform: translate3d(0, 0, 0) scale(1); }
  to { transform: translate3d(28px, -18px, 0) scale(1.08); }
}
@keyframes gridPulse {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 0.75; }
}

.hero,
.ledger,
.create {
  position: relative;
  z-index: 1;
}

.hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1.25rem;
  flex-wrap: wrap;
  margin-bottom: 1.35rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.eyebrow {
  margin: 0 0 0.2rem;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--sea);
  font-weight: 600;
}
.hero h1 {
  margin: 0;
  font-family: Fraunces, Georgia, serif;
  font-size: clamp(2.1rem, 4vw, 3rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  background: linear-gradient(120deg, #fff 20%, #a7f3d0 55%, #5dade2 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: titleSheen 10s ease-in-out infinite;
}
@keyframes titleSheen {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}
.lede {
  margin: 0.45rem 0 0;
  max-width: 34rem;
  color: var(--muted);
  font-size: 0.98rem;
  line-height: 1.45;
}

.mode-tabs {
  display: inline-flex;
  gap: 0.15rem;
  padding: 0.2rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(8px);
}
.mode-tab {
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.58);
  padding: 0.5rem 1.15rem;
  border-radius: 7px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 0.02em;
  transition: color 0.25s ease, background 0.25s ease, transform 0.25s ease;
}
.mode-tab:hover {
  color: #fff;
  transform: translateY(-1px);
}
.mode-tab.active {
  background: linear-gradient(135deg, rgba(26, 188, 156, 0.35), rgba(93, 173, 226, 0.22));
  color: #ecfffa;
  box-shadow: inset 0 0 0 1px rgba(167, 243, 208, 0.25);
}

.ledger-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.type-filter {
  display: inline-flex;
  gap: 0.35rem;
}
.type-chip {
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.7);
  padding: 0.35rem 0.8rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 600;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}
.type-chip:hover { color: #fff; border-color: rgba(255, 255, 255, 0.28); }
.type-chip.active {
  color: #fff;
  border-color: rgba(26, 188, 156, 0.55);
  background: rgba(26, 188, 156, 0.18);
}
.type-chip.order.active {
  border-color: rgba(232, 184, 109, 0.55);
  background: rgba(232, 184, 109, 0.16);
}

.search-wrap {
  flex: 1 1 220px;
  max-width: 360px;
}
.search-wrap input {
  width: 100%;
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  border-radius: 8px;
  padding: 0.55rem 0.75rem;
  font: inherit;
}
.search-wrap input:focus {
  outline: none;
  border-color: rgba(93, 173, 226, 0.65);
  box-shadow: 0 0 0 3px rgba(93, 173, 226, 0.15);
}

.status-line {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.85rem;
  color: var(--muted);
  font-size: 0.9rem;
}
.status-line.error { color: #fca5a5; }
.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--sea);
  box-shadow: 0 0 0 0 rgba(26, 188, 156, 0.55);
  animation: pulse 2.4s ease-out infinite;
}
@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(26, 188, 156, 0.5); }
  70% { box-shadow: 0 0 0 10px rgba(26, 188, 156, 0); }
  100% { box-shadow: 0 0 0 0 rgba(26, 188, 156, 0); }
}

.ledger-stream {
  position: relative;
  max-height: min(68vh, 820px);
  overflow: auto;
  padding: 0.25rem 0.15rem 1rem 0.35rem;
  border-radius: 12px;
  background:
    linear-gradient(180deg, rgba(8, 24, 32, 0.35), rgba(8, 24, 32, 0.12));
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.doc-row {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  gap: 0.65rem 0.85rem;
  align-items: start;
  padding: 0.95rem 0.85rem;
  margin-bottom: 0.35rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid transparent;
  animation: riseIn 0.55s ease both;
  animation-delay: calc(min(var(--i), 12) * 45ms);
  transition: border-color 0.25s, background 0.25s, transform 0.25s;
}
.doc-row:hover {
  background: rgba(26, 188, 156, 0.07);
  border-color: rgba(26, 188, 156, 0.22);
  transform: translateX(3px);
}
@keyframes riseIn {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

.doc-rail {
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: 0.35rem;
  min-height: 100%;
}
.doc-rail::before {
  content: '';
  position: absolute;
  top: 14px;
  bottom: -1.2rem;
  width: 1px;
  background: linear-gradient(180deg, rgba(26, 188, 156, 0.55), rgba(93, 173, 226, 0.15), transparent);
}
.doc-row:last-of-type .doc-rail::before { display: none; }
.doc-node {
  position: relative;
  z-index: 1;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: var(--sea);
  box-shadow: 0 0 0 3px rgba(26, 188, 156, 0.2);
}
.doc-node.order {
  background: var(--amber);
  box-shadow: 0 0 0 3px rgba(232, 184, 109, 0.22);
}

.doc-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.75rem;
}
.doc-id {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.doc-type {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a7f3d0;
}
.doc-type.order { color: #f5d9a6; }
.doc-num {
  font-family: Fraunces, Georgia, serif;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}
.doc-total {
  font-family: Fraunces, Georgia, serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
}

.doc-meta {
  margin-top: 0.2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: baseline;
  font-size: 0.95rem;
}
.contact-link {
  color: #c8f5e9;
  text-decoration: none;
  font-weight: 600;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s, color 0.2s;
}
.contact-link:hover {
  color: #fff;
  border-bottom-color: rgba(167, 243, 208, 0.7);
}
.company { color: var(--muted); }
.sep { color: rgba(255, 255, 255, 0.28); }
.muted { color: var(--muted); }

.doc-sub {
  margin-top: 0.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
}
.ref {
  color: rgba(93, 173, 226, 0.95);
  font-weight: 500;
}

.doc-lines {
  margin: 0.45rem 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.doc-lines li {
  font-size: 0.78rem;
  padding: 0.18rem 0.45rem;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.28);
  color: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.doc-lines .more {
  background: transparent;
  border-style: dashed;
  color: var(--muted);
}

.doc-actions {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  align-items: stretch;
  min-width: 88px;
}
.action {
  text-align: center;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  padding: 0.4rem 0.65rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, transform 0.2s;
}
.action:hover { transform: translateY(-1px); }
.action.pdf {
  border-color: rgba(93, 173, 226, 0.45);
  background: rgba(93, 173, 226, 0.15);
}
.action.danger {
  border-color: rgba(229, 115, 115, 0.45);
  background: rgba(229, 115, 115, 0.12);
  color: #fecaca;
}
.action:disabled { opacity: 0.55; cursor: wait; transform: none; }

.empty-ledger {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--muted);
}
.inline-link {
  background: none;
  border: none;
  color: var(--sea);
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

/* Create form (existing patterns, slightly polished) */
.panel {
  background: rgba(12, 28, 36, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  backdrop-filter: blur(6px);
}
.row {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-bottom: 0.65rem;
}
.grow { flex: 1 1 320px }
.field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 140px;
}
.field label {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.85);
}
.discount-field {
  min-width: 160px;
}
.discount-mode {
  display: inline-flex;
  margin-bottom: 0.25rem;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 6px;
  overflow: hidden;
}
.discount-mode-btn {
  border: none;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  padding: 0.2rem 0.55rem;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
.discount-mode-btn + .discount-mode-btn {
  border-left: 1px solid rgba(255, 255, 255, 0.18);
}
.discount-mode-btn.active {
  background: rgba(26, 188, 156, 0.35);
  color: #ecfffa;
}
.discount-equiv {
  margin: 0.2rem 0 0;
  font-size: 0.75rem;
  color: rgba(167, 243, 208, 0.9);
}
.panel input,
.panel textarea,
.panel select {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  border-radius: 6px;
  padding: 0.45rem;
  font: inherit;
}
textarea { resize: vertical }
.suggestions {
  margin-top: 0.35rem;
  max-height: 180px;
  overflow: auto;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  background: rgba(8, 18, 28, 0.95);
}
.suggestion {
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 0.45rem;
  color: #fff;
  cursor: pointer;
  margin: 0;
}
.suggestion:hover { background: rgba(255, 255, 255, 0.12); }
.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0.5rem;
}
.items-table th,
.items-table td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  padding: 0.4rem;
  text-align: left;
}
.items-table th:nth-child(3),
.items-table th:nth-child(4),
.items-table th:nth-child(5),
.items-table td:nth-child(3),
.items-table td:nth-child(4),
.items-table td:nth-child(5) {
  text-align: right;
}
.danger-btn,
.ghost,
.primary {
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  border: 1px solid transparent;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  color: #fff;
}
.danger-btn {
  background: rgba(231, 76, 60, 0.75);
  border-color: rgba(231, 76, 60, 0.95);
}
.ghost { background: rgba(255, 255, 255, 0.08); border-color: rgba(255, 255, 255, 0.2); }
.primary {
  background: linear-gradient(135deg, rgba(26, 188, 156, 0.9), rgba(14, 122, 102, 0.95));
  border-color: rgba(26, 188, 156, 0.95);
}
.primary:disabled { opacity: 0.5; cursor: not-allowed; }
.totals {
  margin-top: 0.75rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.grand { font-size: 1.05rem }
.actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
}
.form-msg { margin-top: 0.5rem }
.form-msg.error { color: #fca5a5 }
.form-msg.success { color: #86efac }
.selected-contact {
  margin-bottom: 0.55rem;
  color: rgba(255, 255, 255, 0.92);
}
.selected-contact a { color: #a7f3d0 }

.create h2 {
  margin: 0;
  font-family: Fraunces, Georgia, serif;
  font-size: 1.35rem;
}

/* ─── Conduit pipeline ─────────────────────────────────────────────── */
.conduit-pipeline {
  margin-bottom: 1.25rem;
  padding: 1rem 1.15rem;
  border-radius: 18px;
  background: linear-gradient(145deg, rgba(15, 23, 42, 0.72), rgba(30, 41, 59, 0.55));
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}
.pipeline-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
}
.pipeline-head h2 {
  margin: 0;
  font-family: Fraunces, Georgia, serif;
  font-size: 1.2rem;
  color: #f8fafc;
}
.pipeline-sub {
  margin: 0;
  font-size: 0.88rem;
  color: rgba(203, 213, 225, 0.9);
}
.pipeline-stages {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.5rem;
}
.pipeline-stage {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  padding: 0.65rem 0.7rem;
  text-align: left;
  border-radius: 14px;
  border: 1px solid rgba(71, 85, 105, 0.55);
  background: rgba(15, 23, 42, 0.55);
  color: #e2e8f0;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease, background 0.15s ease;
}
.pipeline-stage:hover {
  transform: translateY(-1px);
  border-color: rgba(125, 211, 252, 0.45);
}
.pipeline-stage.active {
  border-color: rgba(56, 189, 248, 0.7);
  background: linear-gradient(160deg, rgba(14, 165, 233, 0.22), rgba(15, 23, 42, 0.7));
  box-shadow: 0 0 0 1px rgba(56, 189, 248, 0.2);
}
.stage-level {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7dd3fc;
}
.stage-label {
  font-size: 0.78rem;
  line-height: 1.25;
  color: rgba(226, 232, 240, 0.92);
  min-height: 2.4em;
}
.stage-count {
  font-size: 1.15rem;
  font-weight: 700;
  color: #f8fafc;
}
.stage-total {
  font-size: 0.78rem;
  color: #a7f3d0;
}
.conduit-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 0.4rem;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: rgba(14, 165, 233, 0.2);
  color: #7dd3fc;
  border: 1px solid rgba(56, 189, 248, 0.35);
}
.conduit-badge[data-level="4"],
.conduit-badge[data-level="5"] {
  background: rgba(16, 185, 129, 0.2);
  color: #6ee7b7;
  border-color: rgba(52, 211, 153, 0.4);
}
.conduit-inline {
  margin: 0.45rem 0 0.35rem;
}
.conduit-select {
  max-width: 100%;
  padding: 0.35rem 0.55rem;
  border-radius: 10px;
  border: 1px solid rgba(71, 85, 105, 0.6);
  background: rgba(15, 23, 42, 0.75);
  color: #e2e8f0;
  font-size: 0.82rem;
}
.conduit-panel {
  margin-bottom: 1rem;
}
.panel-title {
  margin: 0 0 0.25rem;
  font-family: Fraunces, Georgia, serif;
  font-size: 1.25rem;
}
.panel-hint {
  margin: 0 0 0.85rem;
  color: rgba(203, 213, 225, 0.85);
  font-size: 0.9rem;
}
.conduit-level-picker {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.45rem;
  margin-bottom: 1rem;
}
.level-chip {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.65rem 0.55rem;
  border-radius: 14px;
  border: 1px solid rgba(71, 85, 105, 0.55);
  background: rgba(15, 23, 42, 0.55);
  color: #e2e8f0;
  cursor: pointer;
  text-align: left;
}
.level-chip.active {
  border-color: rgba(251, 191, 36, 0.65);
  background: linear-gradient(160deg, rgba(251, 191, 36, 0.22), rgba(15, 23, 42, 0.75));
  box-shadow: 0 0 0 1px rgba(251, 191, 36, 0.2);
}
.level-num {
  font-weight: 700;
  color: #fbbf24;
  font-size: 0.85rem;
}
.level-text {
  font-size: 0.75rem;
  line-height: 1.25;
}
.chibi-contact-field select {
  width: 100%;
}
.chibi-preview {
  margin: 0.4rem 0 0;
  font-size: 0.88rem;
  color: #a7f3d0;
}

@media (max-width: 720px) {
  .doc-row {
    grid-template-columns: 20px minmax(0, 1fr);
  }
  .doc-actions {
    grid-column: 2;
    flex-direction: row;
    min-width: 0;
  }
  .hero h1 { font-size: 2rem }
  .pipeline-stages,
  .conduit-level-picker {
    grid-template-columns: 1fr;
  }
}
</style>